<template>
  <div>
    <v-card-title>
      <v-spacer />
      <v-text-field
        v-model="search"
        append-icon="search"
        label="Filter"
      />
    </v-card-title>

    <v-tooltip
      top
      dark
    >
      <template v-slot:activator="{ on }">
        <v-btn
          class="right-margin"
          color="secondary"
          fab
          dark
          absolute
          top
          right
          @click.stop="showCsvDialog"
          v-on="on"
        >
          <v-icon>file_upload</v-icon>
        </v-btn>
      </template>
      <span>Upload CSV</span>
    </v-tooltip>

    <v-tooltip
      top
      dark
    >
      <template v-slot:activator="{ on }">
        <v-btn
          color="secondary"
          fab
          dark
          absolute
          top
          right
          @click.stop="onCreate"
          v-on="on"
        >
          <v-icon>note_add</v-icon>
        </v-btn>
      </template>
      <span>Create new Bank Information</span>
    </v-tooltip>

    <v-tooltip
      top
      dark
    >
      <template v-slot:activator="{ on }">
        <v-btn
          color="secondary"
          fab
          dark
          absolute
          top
          left
          @click.stop="refresh"
          v-on="on"
        >
          <v-icon>refresh</v-icon>
        </v-btn>
      </template>
      <span>Refresh</span>
    </v-tooltip>

    <v-data-table
      :headers="headers"
      :items="filteredItems"
      :pagination.sync="pagination"
      :loading="isLoading"
      loading-text="Loading..."
      disable-initial-sort
      :rows-per-page-items="[25,50,{'text':'$vuetify.dataIterator.rowsPerPageAll','value':-1}]"
    >
      <template
        slot="items"
        slot-scope="props"
      >
        <tr>
          <td>
            <v-btn
              style="text-transform: none;"
              outline
              round
              color="grey"
              :href="`/#/users/${props.item.user_id}`"
            >
              <v-icon>
                assignment_ind
              </v-icon>
              {{ props.item.user_id }}
            </v-btn>
          </td>
          <td>
            {{ formatIban(props.item.iban) }}
          </td>
          <td>
            {{ props.item.bic }}
          </td>
          <td>
            <DateDisplay
              :date="props.item.signing_updated_at"
            />
          </td>
          <td>
            <DateDisplay
              :date="props.item.created_at"
            />
          </td>
          <td>
            {{ props.item.updated_by }}
          </td>
          <td>
            <DateDisplay
              :date="props.item.updated_at"
            />
          </td>
          <td class="text-xs">
            <v-tooltip
              top
              dark
            >
              <template v-slot:activator="{ on }">
                <v-btn
                  dark
                  flat
                  icon
                  color="primary"
                  v-on="on"
                  @click.stop="() => onEdit(props.item)"
                >
                  <v-icon>edit</v-icon>
                </v-btn>
              </template>
              <span>Edit</span>
            </v-tooltip>
          </td>
        </tr>
      </template>
    </v-data-table>

    <CreateDialog
      v-if="isCreating"
      :is-open="isCreating"
      :item="currentItem"
      :toggle-open="onCreate"
    />

    <UploadCsvDialog
      :visible="uploadCSVDialog"
      @close="hideCsvDialog()"
      @fileUpload="(file) => onUploadCsv(file)"
    />

    <v-dialog
      v-model="csvUploadingDialog"
      persistent
      width="300"
    >
      <v-card
        color="primary"
        dark
      >
        <v-card-text>
          Please wait while uploading CSV.
          <v-progress-linear
            indeterminate
            color="white"
            class="mb-0"
          />
        </v-card-text>
      </v-card>
    </v-dialog>

    <v-dialog
      v-model="csvResultDialog"
      width="800"
    >
      <v-card
        color="primary"
        dark
        class="pa-3"
      >
        <p class="font-weight-black">
          Uploading CSV finished - Result:
        </p>
        <p>Total in CSV: {{ csvUploadingResult.totalInCsv }}</p>
        <p>Total Valid: {{ csvUploadingResult.totalImported }}</p>
        <p>Total Saved to DB: {{ csvUploadingResult.totalDBInserts }}</p>
        <p>Total Failed: {{ csvUploadingResult.totalFailed }}</p>
        <p
          v-for="item in csvUploadingResult.failedReasons"
          :key="item.index"
          style="background-color: red;"
          class="pa-1"
        >
          {{ item.message }}
        </p>
      </v-card>
    </v-dialog>

    <v-snackbar
      v-model="snackbarShow"
      :color="snackbarColor"
      :auto-height="true"
    >
      <ul v-if="snackbarErrors">
        <li
          v-for="snackbarError in snackbarErrors"
          :key="snackbarError.message"
        >
          {{ snackbarError.message }}
        </li>
      </ul>

      <div
        v-if="snackbarMessage"
      >
        {{ snackbarMessage }}
      </div>

      <v-btn
        color="primary"
        text
        @click="snackbarShow = false"
      >
        Close
      </v-btn>
    </v-snackbar>
  </div>
</template>

<script>
import CreateDialog from '@/components/payoutsAndTaxes/payout/dialogs/bankInformation/CreateDialog';
import DateDisplay from '@/components/payoutsAndTaxes/payout/shared/DateDisplay';
import UploadCsvDialog from './dialogs/bankInformation/UploadCsvDialog';

export default {
  name: 'BankInformations',
  components: {
    CreateDialog,
    DateDisplay,
    UploadCsvDialog,
  },
  data() {
    return {
      csvUploadingDialog: false,
      csvUploadingResult: {
        totalDBInserts: 0,
        totalFailed: 0,
        totalImported: 0,
        totalInCsv: 0,
        failedReasons: [],
      },
      csvResultDialog: false,
      snackbarShow: false,
      snackbarMessage: '',
      snackbarColor: 'error',
      snackbarErrors: [],
      selected: 0,
      currentItem: {},
      isLoading: false,
      isCreating: false,
      isShowing: false,
      isEditing: false,
      uploadCSVDialog: false,
      pagination: {
        rowsPerPage: 25,
      },
      search: '',
      headers: [
        { text: 'User ID', value: 'user_id' },
        { text: 'IBAN', value: 'iban' },
        { text: 'BIC', value: 'bic' },
        { text: 'IBAN Updated at', value: 'signing_updated_at' },
        { text: 'Created at', value: 'created_at' },
        { text: 'Updated by', value: 'updated_by' },
        { text: 'Updated at', value: 'updated_at' },
        { text: 'Actions', value: 'actions' },
      ],
    };
  },
  computed: {
    payoutPendingApprovals() {
      return this.$store.getters.getBankInformationsList;
    },
    filteredItems() {
      const { search } = this;

      return search.length < 1 ?
        this.payoutPendingApprovals :
        this.payoutPendingApprovals.filter((item) => {
          let found = false;
          Object.keys(item)
            .map((key) => {
              if (item[key] && String(item[key]).toLowerCase().indexOf(search.toLowerCase()) > -1) {
                found = true;
              }

              return key;
            });

          return found;
        });
    },
  },
  mounted() {
    this.refresh();
  },
  methods: {
    formatIban(value) {
      return value.replace(/[^\dA-Z]/g, '').replace(/(.{4})/g, '$1 ').trim();
    },
    onCreate() {
      this.isCreating = !this.isCreating;
      this.currentItem = {};
    },
    onEdit(item) {
      this.isCreating = !this.isCreating;
      this.currentItem = item;
    },
    showCsvDialog() {
      this.uploadCSVDialog = true;
      this.$forceUpdate();
    },
    hideCsvDialog() {
      this.uploadCSVDialog = false;
      this.$forceUpdate();
    },
    onUploadCsv(file) {
      this.csvUploadingDialog = true;
      this.$store
        .dispatch('callPatBankInformationImportCSV', file)
        .then((e) => {
          this.csvUploadingDialog = false;
          this.csvUploadingResult = e;
          this.csvResultDialog = true;
          this.$store.dispatch('fetchPatBankInformationAll');
        });
    },
    refresh() {
      this.$store.dispatch('fetchPatBankInformationAll');
    },
  },
};
</script>
<style lang="scss">
.right-margin {
  margin-right: 70px;
}
</style>



// WEBPACK FOOTER //
// src/components/payoutsAndTaxes/payout/BankInformations.vue